export const OTP_EXPIRE_TIME = 20000
export const mailServerId = "ifqie4nt"
export const mailServerAddress = "smell-exercise@ifqie4nt.mailosaur.net"

export const domain = "http://192.168.1.30:5000"
export const frontendHost = "http://localhost:3000"
export const testStartingFunds = 1000

export const flags = {
  noAnimation: false,
}
export const minBidDifference = 10
// The pieces a player can be dealt, and can pick as their own in settings. The
// artwork lives in the frontend (utils/data.ts, allPieces); the ids are here so
// the server can validate a saved preference without importing images.
export const ALL_PIECE_IDS = ["0", "1", "2", "3", "4", "5"]
// The auction strategist pays 100 less on every auction he wins, so he is
// allowed to bid 100 above the cash he actually holds.
export const auctionStrategistDiscount = 100
export const getMaxAllowedAuctionBid = (player?: {
  money: number
  role?: GameRoleId
}) =>
  (player?.money ?? 0) +
  (player?.role === "auction-strategist" ? auctionStrategistDiscount : 0)
// The clocks a normal-paced game runs on. A quick game runs shorter ones — see
// getGameTimings below, and reach for that wherever a game is in hand rather
// than importing these directly.
export const turnTotalTime = 100
export const tradeAcceptTime = 40
// How long (seconds) a pending trade sjtays open before it auto-expires.
export const tradeOfferTime = 90
export const auctionTotalTime = 20
export const rolePickingTotalTime = 240
export const cardDecisionTime = 30
export const stockBaseRate = 0.3
export const animationDurationMultiplier = flags.noAnimation ? 0.05 : 1
export const maxTurnsToBeBankrupt = 3
// ── Sharing out a bankrupt player's properties ───────────────────────────────
// A player who goes bankrupt to the bank has their properties split between
// everyone still in the game. The split scores each pile instead of dealing at
// random, so nobody walks away with both of the best properties while somebody
// else gets the two cheapest streets.
//
// Before that though, the split tries not to hand anybody a whole colour. A
// player who lands a complete set can start building on it the same turn, which
// swings a game far harder than the price difference between two properties —
// and it is the likeliest way for a bankruptcy to just hand someone the win. So
// a colour handed over complete is scored as a cost, weighted heavily enough
// that avoiding one always beats evening up the piles. It is a preference and
// not a rule, because sometimes every possible split creates a monopoly; when
// that happens the weighting still steers it towards the cheapest colour.
export const monopolyPenaltyWeight = 8
// A property that takes its new owner *partway* to a colour is worth a little
// more to them than a lone street. Small on purpose: it only decides which
// properties a player gets, never how many, and pushing it higher starts to
// distort the totals.
export const partialSetEstateMultiplier = 1.1
// How many times the split may keep trading properties between piles looking
// for a closer match. It normally settles in one or two passes; the cap is only
// there so an odd board cannot loop.
export const estateBalancingPasses = 6
// Turns-remaining marks at which the whole room gets a heads-up that the turn
// limit is closing in. `situation.turnsLeft` counts full turns (one of these is
// every player playing once), so 5 is the "wrap it up" warning and 1 is the
// final turn itself. Each mark fires once, at the moment that turn starts.
export const endgameWarningTurnsLeft = [5, 1]
// ── Quick Join (the Multiplayer button) ──────────────────────────────────────
// A quick join game normally waits for QUICK_JOIN_MAX_PLAYERS humans. When they
// never show up, the queue gives up after QUICK_JOIN_BOT_FILL_WAIT_TIME seconds
// and fills the empty seats with bots — but only once at least
// QUICK_JOIN_MIN_PLAYERS_FOR_BOT_FILL humans are waiting, so nobody is dropped
// into a "multiplayer" game where every opponent is a bot.
export const QUICK_JOIN_MAX_PLAYERS = 4
export const QUICK_JOIN_MIN_PLAYERS_FOR_BOT_FILL = 2
// Board every quick join game is played on ("1" is the mini map). Doubles as
// the queue key, so everyone waiting is waiting for the same board.
export const QUICK_JOIN_MAP_ID = "1"
// How long (seconds) the queue waits before filling the rest with bots.
export const QUICK_JOIN_BOT_FILL_WAIT_TIME = 20
// How long (seconds) the "N seats filled by bots" notice stays on screen before
// the game actually starts. Also the window the bots use to connect.
export const QUICK_JOIN_BOT_FILL_NOTICE_TIME = 4
// Idle auto-end, in two stages. The active player has to sit completely still
// (no click, key, scroll or touch anywhere) for `autoEndTurnIdleTime` seconds
// before anything happens; only then does the visible `autoEndTurnTime`
// countdown appear, and any input during it puts them back to square one. So a
// turn is lost after idleTime + time of genuine inactivity, never less.
// The server's `turnTotalTime` timer is still the hard backstop underneath —
// this only shortens the wait for a player who is present but idle.
export const autoEndTurnIdleTime = 7000
export const autoEndTurnTime = 7000
// How long (seconds) a disconnected player's seat is held for them before a bot
// takes it over. Long enough to survive a reload or a brief network blip, short
// enough that the rest of the table is not left waiting on a seat nobody is
// sitting in. Everyone in the room watches this count down on the player's card.
export const disconnectedPlayerBotTakeoverTime = 60
export const maxCardsPerTurn = 20
export const botTurnWaitingTime = 2.5
export const BOT_TRADE_ACCEPT_TIME = 2000
export const BOT_TRADE_GAP_TIME = 3000
// Number of own turns after which a bot forgets the trades it already offered
export const BOT_TRADE_HISTORY_CLEAR_TURNS = 4
// How often (ms) every game's bots are given a chance to act. Each game is
// polled on its own schedule, so a quick game's bots come round more often and
// leave less dead air between turns.
export const BOT_MOVE_INTERVAL = 1500
export const QUICK_GAME_BOT_MOVE_INTERVAL = 800

// `quick` is the per-game scale on top of the global one: 1 for a normal game,
// QUICK_GAME_ANIMATION_MULTIPLIER for a quick one. Reach for these through
// getAnimationTimings(game) rather than the bare `animationTimiings` object
// wherever a game is in hand — the server, the client and the bot all wait on
// these numbers independently, so a call site left on the normal set is a call
// site where one of them advances while another is still animating.
const buildAnimationTimings = (quick: number) => ({
  goToJail: animationDurationMultiplier * quick * 2500,
  drawChanceCard: animationDurationMultiplier * quick * 3000,
  drawAndKeepCard: animationDurationMultiplier * quick * 3000,
  playActionCard: animationDurationMultiplier * quick * 3000,
  diceRoll: animationDurationMultiplier * quick * 1000,

  auctionResultNotification: animationDurationMultiplier * quick * 4000,
  propertyTransfer: animationDurationMultiplier * quick * 800,
  houseRemove: animationDurationMultiplier * quick * 1500,
  houseAdd: animationDurationMultiplier * quick * 500,
  houseAddDelay: animationDurationMultiplier * quick * 250,
  houseRemoveDelay: animationDurationMultiplier * quick * 250,
  mortgage: animationDurationMultiplier * quick * (1600 + 100),
  unMortgage: animationDurationMultiplier * quick * (2200 + 100),
  showAuctionBids: animationDurationMultiplier * quick * 450000,
  tradeNotificaiton: quick * 4000,
  bankruptNotification: quick * 2000,
  pieceMovementJump: animationDurationMultiplier * quick * 250,
  pieceMovementWithoutJump: animationDurationMultiplier * quick * 2000,
  privateBuyNotification: animationDurationMultiplier * quick * 2000,
  auctionNotificationDelay: animationDurationMultiplier * quick * 2500,
  // How long a property card keeps glowing after it settles into the new
  // owner's panel (i.e. after landing), when bought/trad400.
  propertyAcquiredGlow: animationDurationMultiplier * quick * 5000,
  // How long the traded property cards glow in place after a successful trade
  // (trades apply instantly — the glow is the only animation).
  tradeSuccessGlow: animationDurationMultiplier * quick * 5000,
})

export type AnimationTimings = ReturnType<typeof buildAnimationTimings>

// How much of their normal length animations run at in a quick game.
export const QUICK_GAME_ANIMATION_MULTIPLIER = 0.6

export const animationTimiings = buildAnimationTimings(1)
const quickGameAnimationTimings = buildAnimationTimings(
  QUICK_GAME_ANIMATION_MULTIPLIER
)

type GameWithSettings =
  | { settings?: { isQuickGame?: boolean } }
  | null
  | undefined

// The timings one specific game runs at. Falls back to the normal set when
// there is no game in hand (or it isn't a quick one).
export const getAnimationTimings = (
  game?: GameWithSettings
): AnimationTimings =>
  game?.settings?.isQuickGame ? quickGameAnimationTimings : animationTimiings

// The same scale for the one-off durations that aren't in the table above
// (GSAP tweens measured in seconds, staggered beam delays, and so on).
export const getAnimationMultiplier = (game?: GameWithSettings): number =>
  animationDurationMultiplier *
  (game?.settings?.isQuickGame ? QUICK_GAME_ANIMATION_MULTIPLIER : 1)

// ── Clocks, per game pace ────────────────────────────────────────────────────
// A quick game is the same game on shorter fuses: every clock a player waits on
// is cut down, not just the animations. These are deliberately not proportional
// to the normal set — a turn is less than half as long, but a card decision
// still needs enough seconds to read the card, so it only halves.
//
// Same rule as getAnimationTimings: go through getGameTimings(game) wherever a
// game is in hand. The bare constants above are the normal-pace set and are
// only correct where there is genuinely no game yet (initial store state).
const normalGameTimings = {
  turnTotalTime,
  auctionTotalTime,
  rolePickingTotalTime,
  cardDecisionTime,
  autoEndTurnIdleTime,
  autoEndTurnTime,
  botTurnWaitingTime,
}
const quickGameTimings: typeof normalGameTimings = {
  turnTotalTime: 45,
  auctionTotalTime: 17,
  rolePickingTotalTime: 80,
  cardDecisionTime: 15,
  autoEndTurnIdleTime: 5,
  autoEndTurnTime: 5,
  botTurnWaitingTime: 1.5,
}

export type GameTimings = typeof normalGameTimings

export const getGameTimings = (game?: GameWithSettings): GameTimings =>
  game?.settings?.isQuickGame ? quickGameTimings : normalGameTimings

// ── Switching a running game over to fast pace ───────────────────────────────
// A game does not have to be born quick. Anybody still playing can put it to
// the table mid-game, and it only carries if every one of them agrees — it
// shortens the clock the others are playing on, so one person cannot impose it.
//
// The window is deliberately generous: the proposal lands in the middle of
// somebody else's turn, so it has to outlast them finishing a trade or a build
// before they even look up at it. Not answering in time counts as a no, which
// is the whole reason it can afford to be this long.
export const FAST_PACE_VOTE_TIME = 45
// How long a bot sits on the proposal before answering it. Bots always accept
// (see bot/main.ts), so this is purely so the tally visibly fills in instead of
// being over before the notification has finished animating in.
export const BOT_FAST_PACE_VOTE_TIME = 1200
// Below this many finished games an account still counts as new, and fast pace
// gets explained before it is handed over rather than after. Only ever used to
// decide whether to *warn* somebody — nothing is withheld from a new player.
export const NEW_PLAYER_GAMES_THRESHOLD = 4
export const maxHousesPerProperty = 3
// Both tax action cards charge a flat rate against what the target owns, and
// the rate varies per map. Shared so the rate the card picker advertises can't
// drift from what actually gets charged in allChances.
export const getPropertyTaxPerProperty = (mapId: string) =>
  mapId === "0" ? 25 : mapId === "1" ? 40 : 50
export const getHouseTaxPerHouse = (mapId: string) => (mapId === "1" ? 30 : 50)
// Action cards whose whole point is an amount of money, and which way that
// money flows: "charged" is taken off the target and handed to the bank,
// "collected" is gathered in by the player who played the card. The game log
// records what actually moved for these — the card name alone never says how
// much, and the rate depends on the map and on what the target owns.
export const cardMoneyFlow: Partial<
  Record<ActionCardId, "charged" | "collected">
> = {
  "perform-tax-audit": "charged",
  "apply-property-tax": "charged",
  "apply-house-tax": "charged",
  "get-10-percent-from-all-players": "collected",
  "get-10-percent-from-player": "collected",
}
export const allRoles: GameRoleId[] = [
  "auction-strategist",
  "card-stealer",
  "private-buyer",
  "rent-lord",
  "the-constructor",
  "wall-street-wolf",
  "financer",
]
export const isDemoMode = false
export const maxInJailTurns = 3
const demoFeatureFlags: FeatureFlags = {
  allowQuickJoin: false,
  allowCustomLobby: false,
  navbarLinks: false,
  showTestSettings: false,
  allowStandardMapInSinglePlayerMode: false,
  showChat: false,
  confirmBeforeLeavingGame: false,
  disabledRoles: ["private-buyer", "financer", "rent-lord"],
  allowedChallenges: [
    "win-by-bankruptcy",
    "start-with-1200-money",
    "win-as-rent-lord-without-houses",
  ],
}
const normalFeatureFlags: FeatureFlags = {
  allowQuickJoin: true,
  allowCustomLobby: true,
  navbarLinks: true,
  showTestSettings: true,
  allowStandardMapInSinglePlayerMode: true,
  showChat: false,
  confirmBeforeLeavingGame: true,
  disabledRoles: [],
  // Only these three are playable for now; the rest show a "Coming Soon" badge.
  allowedChallenges: [
    "win-by-bankruptcy",
    "start-with-1200-money",
    "win-as-rent-lord-without-houses",
  ],
}
export interface FeatureFlags {
  allowQuickJoin: boolean
  allowCustomLobby: boolean
  navbarLinks: boolean
  showTestSettings: boolean
  allowStandardMapInSinglePlayerMode: boolean
  showChat: boolean
  confirmBeforeLeavingGame: boolean
  disabledRoles: GameRoleId[]
  allowedChallenges: ChallengeId[]
}
export const featureFlags: FeatureFlags = isDemoMode
  ? demoFeatureFlags
  : normalFeatureFlags
export interface ITestUserData {
  username: string
  email: string
  password: string
}
export const testConsts = {
  OTP_EXPIRE_TIME: 20000,
  mailServerId: "ifqie4nt",
  mailServerAddress: "smell-exercise@ifqie4nt.mailosaur.net",
  domain: "http://localhost:5000",
  frontendHost: "http://localhost:3000",
  users: [
    {
      username: "user1",
      email: "maheerali121@gmail.com",
      password: "Xcvsdf@12345",
    },
    {
      username: "user2",
      email: "maheersaimoon@gmail.com",
      password: "Xcvsdf@12345",
    },
    {
      username: "user3",
      email: "maheerali181@gmail.com",
      password: "Xcvsdf@12345",
    },
    {
      username: "user4",
      email: "maheerali171@gmail.com",
      password: "Xcvsdf@12345",
    },
    {
      username: "user5",
      email: "maheerali161@gmail.com",
      password: "Xcvsdf@12345",
    },
    {
      username: "user6",
      email: "test1@gmail.com",
      password: "Xcvsdf@12345",
    },
    {
      username: "user7",
      email: "test2@gmail.com",
      password: "Xcvsdf@12345",
    },
    {
      username: "user8",
      email: "test3@gmail.com",
      password: "Xcvsdf@12345",
    },
    {
      username: "user9",
      email: "test4@gmail.com",
      password: "Xcvsdf@12345",
    },
    {
      username: "user10",
      email: "test5@gmail.com",
      password: "Xcvsdf@12345",
    },
  ] as ITestUserData[],
}
